import i18next, { type i18n } from "i18next";
import { initReactI18next } from "react-i18next";
import { en, type Catalog } from "./locales/en";
import { fil } from "./locales/fil";

export const SUPPORTED_LANGUAGES = ["en", "fil"] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];
export const DEFAULT_LANGUAGE: Language = "en";
export const LANGUAGE_STORAGE_KEY = "sg-lang";

export const CATALOGS: Readonly<Record<Language, Catalog>> = { en, fil };

export function isLanguage(value: string): value is Language {
  return SUPPORTED_LANGUAGES.some((language) => language === value);
}

function storedLanguage(): Language | null {
  try {
    const value = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return value && isLanguage(value) ? value : null;
  } catch {
    return null;
  }
}

/** Initializes a dedicated i18next instance bound to react-i18next. */
export async function createI18n(language?: Language): Promise<i18n> {
  const instance = i18next.createInstance();
  await instance.use(initReactI18next).init({
    lng: language ?? (typeof window === "undefined" ? DEFAULT_LANGUAGE : (storedLanguage() ?? DEFAULT_LANGUAGE)),
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: [...SUPPORTED_LANGUAGES],
    resources: {
      en: { translation: en },
      fil: { translation: fil },
    },
    interpolation: { escapeValue: false },
    returnNull: false,
  });
  return instance;
}

export { I18nextProvider, useTranslation } from "react-i18next";
export { en, fil, type Catalog };
export { formatMoney, moneyLocale, type MoneyOptions } from "./money";
export type { i18n as I18nInstance } from "i18next";
